import React, { useState, useEffect } from "react";
import axios from "axios";

/**
 * SurveyInviteWidget
 * ──────────────────
 * Self-contained invite + QR widget that can be dropped into any
 * Global*Responses dashboard.
 *
 * Props:
 *   surveyId   {string|number}  – the survey's ID
 *   companyId  {string|number}  – defaults to "0" (public/individual survey)
 *   label      {string}         – optional heading override
 */
export default function SurveyInviteWidget({ surveyId, companyId = "0", label }) {
  const [open,       setOpen]       = useState(false);
  const [ttlOption,  setTtlOption]  = useState("24");   // "24" | "48" | "custom" | "unlimited"
  const [customHrs,  setCustomHrs]  = useState(72);
  const [surveyLink, setSurveyLink] = useState("");
  const [copied,     setCopied]     = useState(false);
  const [qrCode,     setQrCode]     = useState("");
  const [qrLoading,  setQrLoading]  = useState(false);
  const [qrError,    setQrError]    = useState(false);

  // ── Derive effective hours from the selected TTL option ─────────────────────
  const effectiveHours =
    ttlOption === "unlimited" ? null
    : ttlOption === "custom" ? Number(customHrs) || 24
    : Number(ttlOption);

  // ── Rebuild link whenever surveyId / companyId / TTL changes ────────────────
  useEffect(() => {
    if (!surveyId) return;

    const baseUrl = window.location.origin;
    const isIndividual = String(companyId) === "0";

    const payload = {
      surveyId: String(surveyId),
      company_fk: String(companyId),
    };

    if (isIndividual && effectiveHours !== null) {
      payload.expiresAt = Date.now() + effectiveHours * 60 * 60 * 1000;
    }
    // effectiveHours === null ("unlimited") → no expiresAt embedded → link never expires

    const token = btoa(encodeURIComponent(JSON.stringify(payload)));
    setSurveyLink(`${baseUrl}/survey/${token}`);

    // Reset QR whenever link changes
    setQrCode("");
    setQrError(false);
  }, [surveyId, companyId, effectiveHours]);

  // ── Fetch QR whenever surveyLink changes (and widget is open) ───────────────
  useEffect(() => {
    if (!surveyLink || !open) return;
    fetchQr();
  }, [surveyLink, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchQr = async () => {
    setQrLoading(true);
    setQrError(false);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/survey/qr?url=${encodeURIComponent(surveyLink)}`
      );
      setQrCode(res.data.qr || "");
    } catch {
      setQrError(true);
    } finally {
      setQrLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(surveyLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleDownloadQR = () => {
    const a = document.createElement("a");
    a.href     = qrCode;
    a.download = `survey-${surveyId}-${companyId}-qr.png`;
    a.click();
  };

  // ── TTL label helper ────────────────────────────────────────────────────────
  const ttlLabel =
    ttlOption === "24"        ? "24 hrs"
    : ttlOption === "48"      ? "48 hrs"
    : ttlOption === "unlimited" ? "Unlimited"
    : `${customHrs} hrs (custom)`;

  // ── Styles (all inline so zero CSS-file dependency) ─────────────────────────
  const S = {
    wrapper: {
      border:       "1px solid #e2e8f0",
      borderRadius: "10px",
      marginBottom: "16px",
      overflow:     "hidden",
      background:   "#fff",
    },
    toggleBtn: {
      width:          "100%",
      display:        "flex",
      justifyContent: "space-between",
      alignItems:     "center",
      padding:        "12px 16px",
      background:     open ? "#f0fdf4" : "#f8fafc",
      border:         "none",
      cursor:         "pointer",
      fontWeight:     700,
      fontSize:       "13px",
      color:          "#1e293b",
      borderBottom:   open ? "1px solid #e2e8f0" : "none",
      transition:     "background 0.2s",
    },
    body: {
      padding: "16px",
    },
    row: {
      display:       "flex",
      flexWrap:      "wrap",
      gap:           "12px",
      alignItems:    "flex-end",
      marginBottom:  "14px",
    },
    label: {
      fontSize:    "11px",
      fontWeight:  700,
      color:       "#64748b",
      display:     "block",
      marginBottom: "4px",
      textTransform: "uppercase",
      letterSpacing: ".05em",
    },
    select: {
      padding:      "7px 10px",
      borderRadius: "6px",
      border:       "1px solid #cbd5e1",
      fontSize:     "13px",
      background:   "#fff",
      cursor:       "pointer",
    },
    numberInput: {
      padding:      "7px 10px",
      borderRadius: "6px",
      border:       "1px solid #cbd5e1",
      fontSize:     "13px",
      width:        "80px",
    },
    linkInput: {
      flex:         1,
      padding:      "8px 10px",
      borderRadius: "6px",
      border:       "1px solid #cbd5e1",
      fontSize:     "12px",
      fontFamily:   "monospace",
      color:        "#334155",
      background:   "#f8fafc",
      minWidth:     "0",
    },
    copyBtn: {
      padding:      "8px 14px",
      background:   "#0f172a",
      color:        "#fff",
      border:       "none",
      borderRadius: "6px",
      cursor:       "pointer",
      fontSize:     "12px",
      fontWeight:   700,
      whiteSpace:   "nowrap",
    },
    refreshBtn: {
      padding:      "8px 14px",
      background:   "#475569",
      color:        "#fff",
      border:       "none",
      borderRadius: "6px",
      cursor:       "pointer",
      fontSize:     "12px",
      fontWeight:   700,
      whiteSpace:   "nowrap",
    },
    downloadBtn: {
      marginTop:    "10px",
      padding:      "8px 16px",
      background:   "#16a34a",
      color:        "#fff",
      border:       "none",
      borderRadius: "6px",
      cursor:       "pointer",
      fontSize:     "12px",
      fontWeight:   700,
    },
    ttlBadge: {
      display:      "inline-block",
      background:   "#eff6ff",
      color:        "#3b5ec0",
      border:       "1px solid #bfdbfe",
      borderRadius: "999px",
      padding:      "2px 10px",
      fontSize:     "11px",
      fontWeight:   700,
      marginLeft:   "8px",
    },
    qrBox: {
      marginTop:   "16px",
      display:     "flex",
      flexDirection: "column",
      alignItems:  "flex-start",
      gap:         "8px",
    },
    qrImg: {
      width:        "180px",
      height:       "180px",
      border:       "1px solid #e2e8f0",
      borderRadius: "8px",
      padding:      "6px",
      background:   "#fff",
    },
    spinner: {
      width:        "180px",
      height:       "180px",
      display:      "flex",
      alignItems:   "center",
      justifyContent: "center",
      color:        "#94a3b8",
      fontSize:     "12px",
      border:       "1px solid #e2e8f0",
      borderRadius: "8px",
    },
  };

  return (
    <div style={S.wrapper}>
      {/* ── Toggle header ── */}
      <button style={S.toggleBtn} onClick={() => setOpen(o => !o)}>
        <span>
          🔗 {label || "Generate Invite Link & QR Code"}
          <span style={S.ttlBadge}>TTL: {ttlLabel}</span>
        </span>
        <span style={{ fontSize: "16px", color: "#64748b" }}>{open ? "▲" : "▼"}</span>
      </button>

      {/* ── Collapsible body ── */}
      {open && (
        <div style={S.body}>

          {/* TTL selector row */}
          <div style={S.row}>
            <div>
              <span style={S.label}>Link Expiry (Time to Live)</span>
              <select
                style={S.select}
                value={ttlOption}
                onChange={e => { setTtlOption(e.target.value); }}
              >
                <option value="24">24 hours (default)</option>
                <option value="48">48 hours</option>
                <option value="custom">Custom hours</option>
                <option value="unlimited">Unlimited (no expiry)</option>
              </select>
            </div>

            {ttlOption === "custom" && (
              <div>
                <span style={S.label}>Custom Hours</span>
                <input
                  type="number"
                  min={1}
                  max={8760}
                  style={S.numberInput}
                  value={customHrs}
                  onChange={e => setCustomHrs(e.target.value)}
                  placeholder="e.g. 72"
                />
              </div>
            )}

            <div style={{ alignSelf: "flex-end" }}>
              <button style={S.refreshBtn} onClick={fetchQr} disabled={qrLoading}>
                {qrLoading ? "⏳ Loading..." : "🔄 Refresh QR"}
              </button>
            </div>
          </div>

          {/* Link row */}
          <div>
            <span style={S.label}>Generated Survey Link</span>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                style={S.linkInput}
                type="text"
                value={surveyLink}
                readOnly
              />
              <button style={S.copyBtn} onClick={handleCopy}>
                {copied ? "✅ Copied!" : "📋 Copy"}
              </button>
            </div>
          </div>

          {/* QR code */}
          <div style={S.qrBox}>
            <span style={S.label}>QR Code</span>
            {qrLoading ? (
              <div style={S.spinner}>Generating QR…</div>
            ) : qrError ? (
              <div style={{ ...S.spinner, color: "#ef4444" }}>Failed to generate QR</div>
            ) : qrCode ? (
              <>
                <img src={qrCode} alt="Survey QR Code" style={S.qrImg} />
                <button style={S.downloadBtn} onClick={handleDownloadQR}>
                  💾 Download QR Code (PNG)
                </button>
              </>
            ) : (
              <div style={S.spinner}>QR will appear here</div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}